/**
 * PermissionGate - React component for conditional rendering based on permissions
 * 
 * This component provides a clean way to conditionally render UI elements
 * based on user permissions.
 */

import React from 'react';
import { usePermissionChecker, PERMISSION_RESOURCES, PERMISSION_ACTIONS } from '../hooks/usePermissionChecker';

export interface PermissionGateProps {
  children: React.ReactNode;
  resource?: string;
  action?: string;
  fallback?: React.ReactNode;
  requireAll?: boolean; // If true, user must have ALL specified permissions
  permissions?: Array<{ resource: string; action: string }>; // Multiple permissions to check
  role?: string; // Check for specific role instead of permission
  invert?: boolean; // Invert the permission check (show when user doesn't have permission)
}

/**
 * PermissionGate component
 * 
 * Usage examples:
 * 
 * 1. Basic permission check:
 * <PermissionGate resource="users" action="create">
 *   <CreateUserButton />
 * </PermissionGate>
 * 
 * 2. Multiple permissions (any):
 * <PermissionGate 
 *   permissions={[
 *     { resource: 'users', action: 'create' },
 *     { resource: 'users', action: 'update' }
 *   ]}
 * >
 *   <UserActions />
 * </PermissionGate>
 * 
 * 3. Multiple permissions (all):
 * <PermissionGate 
 *   permissions={[
 *     { resource: 'users', action: 'create' },
 *     { resource: 'roles', action: 'read' }
 *   ]}
 *   requireAll={true}
 * >
 *   <AdvancedUserActions />
 * </PermissionGate>
 * 
 * 4. Role-based check:
 * <PermissionGate role="admin">
 *   <AdminPanel />
 * </PermissionGate>
 * 
 * 5. With fallback:
 * <PermissionGate 
 *   resource="users" 
 *   action="delete"
 *   fallback={<span>You don't have permission to delete users</span>}
 * >
 *   <DeleteUserButton />
 * </PermissionGate>
 * 
 * 6. Inverted permission check:
 * <PermissionGate 
 *   resource="users" 
 *   action="create"
 *   invert={true}
 * >
 *   <span>You cannot create users</span>
 * </PermissionGate>
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  resource,
  action,
  fallback = null,
  requireAll = false,
  permissions,
  role,
  invert = false,
}) => {
  const { hasPermission, hasRole, hasAnyPermission, hasAllPermissions } = usePermissionChecker();

  // Determine if user has the required permissions
  const hasRequiredPermission = React.useMemo(() => {
    // Role-based check
    if (role) {
      const hasRequiredRole = hasRole(role);
      return invert ? !hasRequiredRole : hasRequiredRole;
    }

    // Single permission check
    if (resource && action) {
      const hasSinglePermission = hasPermission(resource, action);
      return invert ? !hasSinglePermission : hasSinglePermission;
    }

    // Multiple permissions check
    if (permissions && permissions.length > 0) {
      const hasMultiplePermissions = requireAll 
        ? hasAllPermissions(permissions)
        : hasAnyPermission(permissions);
      return invert ? !hasMultiplePermissions : hasMultiplePermissions;
    }

    // Default to false if no valid permission check is provided
    return false;
  }, [resource, action, permissions, requireAll, role, invert, hasPermission, hasRole, hasAnyPermission, hasAllPermissions]);

  // Render children if user has permission, otherwise render fallback
  return hasRequiredPermission ? <>{children}</> : <>{fallback}</>;
};

/**
 * Convenience components for common permission checks
 */

export const CanRead: React.FC<Omit<PermissionGateProps, 'action'>> = (props) => (
  <PermissionGate {...props} action={PERMISSION_ACTIONS.READ} />
);

export const CanCreate: React.FC<Omit<PermissionGateProps, 'action'>> = (props) => (
  <PermissionGate {...props} action={PERMISSION_ACTIONS.CREATE} />
);

export const CanUpdate: React.FC<Omit<PermissionGateProps, 'action'>> = (props) => (
  <PermissionGate {...props} action={PERMISSION_ACTIONS.UPDATE} />
);

export const CanDelete: React.FC<Omit<PermissionGateProps, 'action'>> = (props) => (
  <PermissionGate {...props} action={PERMISSION_ACTIONS.DELETE} />
);

/**
 * Resource-specific convenience components
 */

export const CanReadUsers: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.USERS} action={PERMISSION_ACTIONS.READ} />
);

export const CanCreateUsers: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.USERS} action={PERMISSION_ACTIONS.CREATE} />
);

export const CanUpdateUsers: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.USERS} action={PERMISSION_ACTIONS.UPDATE} />
);

export const CanDeleteUsers: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.USERS} action={PERMISSION_ACTIONS.DELETE} />
);

export const CanReadRoles: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.ROLES} action={PERMISSION_ACTIONS.READ} />
);

export const CanCreateRoles: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.ROLES} action={PERMISSION_ACTIONS.CREATE} />
);

export const CanUpdateRoles: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.ROLES} action={PERMISSION_ACTIONS.UPDATE} />
);

export const CanDeleteRoles: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.ROLES} action={PERMISSION_ACTIONS.DELETE} />
);

export const CanReadCompanies: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.COMPANIES} action={PERMISSION_ACTIONS.READ} />
);

export const CanCreateCompanies: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.COMPANIES} action={PERMISSION_ACTIONS.CREATE} />
);

export const CanUpdateCompanies: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.COMPANIES} action={PERMISSION_ACTIONS.UPDATE} />
);

export const CanDeleteCompanies: React.FC<Omit<PermissionGateProps, 'resource' | 'action'>> = (props) => (
  <PermissionGate {...props} resource={PERMISSION_RESOURCES.COMPANIES} action={PERMISSION_ACTIONS.DELETE} />
);

/**
 * Higher-order component for wrapping components with permission checks
 */
export function withPermission<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  resource: string,
  action?: string,
  fallback?: React.ReactNode
) {
  const WithPermissionComponent: React.FC<P> = (props) => (
    <PermissionGate resource={resource} action={action} fallback={fallback}>
      <WrappedComponent {...props} />
    </PermissionGate>
  );

  WithPermissionComponent.displayName = `withPermission(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithPermissionComponent;
}

export default PermissionGate;
